import type { SourceCardImage } from '@digital-twins/shared-types'
import { sourceCardImageKey, sourceCardImageSrc } from '../sourcePreviews'
import { useMemo } from 'react'

import { CitationImage, CitationSource, httpsImageSrc } from '@/components/citations'
import { Text } from '@/components/ui/Text'
import { getThreadPlaceholders } from '@/lib/constants/languages'
import { ThreadAssetView } from '@/lib/api'
import { createBoundaryParser } from '@/lib/validation/schema-loader'
import richBlockSchema from '../../../../../../packages/shared-types/schemas/assistant/rich-blocks.schema.json'
import { citationImage } from '../citationImage'
import { CitationSourceContext, citationSourceForTarget } from '../citationSources'
import { EvidenceCitationTarget, PartitionedEvidenceV2 } from '../partitionAssistantCitations'
import { KpiRow } from './KpiRow'
import { TrendCards } from './TrendCards'
import { ThreadStepGlyph } from '../ThreadStepGlyph'
import { markdownLinkUrls } from './RichInlineText'
import { citationSocialPost } from '@/pages/KnowledgeHubPage/social/SocialCitationPostMeta'

import {
  FairgenCardsBlock,
  FairgenKpisBlock,
  RichCard,
  RichKpi,
} from '@digital-twins/shared-types/assistant-rich-blocks.generated'
import { SocialCitationPost } from '@digital-twins/shared-types'

export type RichBlockLanguage = 'fairgen-kpis' | 'fairgen-cards'

const RICH_BLOCK_LANGUAGES: Array<RichBlockLanguage> = ['fairgen-kpis', 'fairgen-cards']

const EVIDENCE_ID = /^(?:\[\[)?(E[1-9][0-9]*)(?:\]\])?$/

const ASSET_REF = /^asset:([A-Za-z0-9_-]{1,64})$/

// One parser per fence, both validated against the python-owned schema (D6).
const parseKpis = createBoundaryParser<FairgenKpisBlock>('assistant/fairgen-kpis', {
  $defs: richBlockSchema.$defs,
  $ref: '#/$defs/FairgenKpisBlock',
})
const parseCards = createBoundaryParser<FairgenCardsBlock>('assistant/fairgen-cards', {
  $defs: richBlockSchema.$defs,
  $ref: '#/$defs/FairgenCardsBlock',
})

/** The subset of a react-markdown (hast) node the `pre` handler reads. */
export interface MarkdownNodeLike {
  type?: string
  tagName?: string
  value?: string
  properties?: { className?: unknown }
  children?: Array<MarkdownNodeLike>
}

export interface RichFence {
  /** The fence language, or a prefix of one while its info string is still streaming. */
  language: string
  isComplete: boolean
  source: string
}

interface RichBlockProps {
  fence: RichFence
  /** The answer is still streaming or revealing. An unparsable fence shows the preparing row until it parses. */
  isPending: boolean
  /** Thread language for the preparing row. Absent falls back to English. */
  language?: string | null
  citationV2?: PartitionedEvidenceV2
  sourceContext: CitationSourceContext
  threadAssets?: Array<ThreadAssetView>
  /** The org's stored social posts behind the answer's post links, keyed by link. */
  linkedPosts?: Map<string, SocialCitationPost>
  cardImages?: Array<SourceCardImage>
  sourceImagesLoading?: boolean
}

function nodeText(node: MarkdownNodeLike | undefined): string {
  if (!node) {
    return ''
  }
  if (typeof node.value === 'string') {
    return node.value
  }
  return (node.children ?? []).map(nodeText).join('')
}

function fenceLanguage(node: MarkdownNodeLike | undefined): string | null {
  const className = node?.properties?.className
  const classes = Array.isArray(className) ? className : typeof className === 'string' ? className.split(' ') : []
  for (const entry of classes) {
    if (typeof entry === 'string' && entry.startsWith('language-')) {
      return entry.slice('language-'.length)
    }
  }
  return null
}

/**
 * A `pre` node that holds a `fairgen-*` fence, or null for any other code
 * block. While the answer streams, a fence whose info string is only a prefix
 * so far (`fairgen-k`) is already claimed, so raw JSON never flashes.
 */
export function richFenceFromPre(node: MarkdownNodeLike | undefined, isPending: boolean): RichFence | null {
  const code = node?.children?.find((child) => child.tagName === 'code')
  const language = fenceLanguage(code)
  if (!language) {
    return null
  }
  const isComplete = RICH_BLOCK_LANGUAGES.includes(language as RichBlockLanguage)
  const isPrefix = isPending && language.length >= 3 &&
    RICH_BLOCK_LANGUAGES.some((candidate) => candidate.startsWith(language))
  if (!isComplete && !isPrefix) {
    return null
  }
  return { language, isComplete, source: nodeText(code) }
}

/** `E3` or `[[E3]]` → `E3`; anything else → null. */
export function evidenceIdOf(ref: string): string | null {
  const match = EVIDENCE_ID.exec(ref.trim())
  return match ? match[1] : null
}

function citeList(cite: string | Array<string> | null | undefined): Array<string> {
  if (!cite) {
    return []
  }
  return Array.isArray(cite) ? cite : [cite]
}

function targetEvidenceId(target: EvidenceCitationTarget | null): string | null {
  if (!target) {
    return null
  }
  if (target.kind === 'web') {
    return target.citation.id
  }
  return target.evidence.id
}

function parseJson(source: string): unknown {
  try {
    return JSON.parse(source)
  } catch {
    return undefined
  }
}

/**
 * A `fairgen-kpis` or `fairgen-cards` fence rendered natively (D6). `cite`
 * maps to the resolved evidence item by its `id`, never by position. While
 * the answer streams, a fence that does not parse yet shows the same status
 * row as a search step. Once the answer settles, a block that does not match
 * the contract renders nothing.
 */
export function RichBlock(props: RichBlockProps) {
  const { fence, isPending, language, citationV2, sourceContext, threadAssets, linkedPosts, cardImages, sourceImagesLoading } = props

  const parsed = useMemo(() => {
    if (!fence.isComplete) {
      return null
    }
    const value = parseJson(fence.source)
    if (value === undefined) {
      return null
    }
    if (fence.language === 'fairgen-kpis') {
      const result = parseKpis(value)
      return result.ok ? { kind: 'kpis' as const, kpis: result.data } : null
    }
    const result = parseCards(value)
    return result.ok ? { kind: 'cards' as const, cards: result.data } : null
  }, [fence.isComplete, fence.language, fence.source])

  const targetsById = useMemo(() => {
    const byId = new Map<string, { target: EvidenceCitationTarget; index: number }>()
    citationV2?.targets.forEach((target, index) => {
      const id = targetEvidenceId(target)
      if (target && id && !byId.has(id)) {
        byId.set(id, { target, index })
      }
    })
    return byId
  }, [citationV2])

  const assetsByToken = useMemo(
    () => new Map((threadAssets ?? []).map((asset) => [asset.token, asset])),
    [threadAssets],
  )

  const sourcesFor = (cite: string | Array<string> | null | undefined, claim: string): Array<CitationSource> => {
    const sources: Array<CitationSource> = []
    const seen = new Set<string>()
    for (const ref of citeList(cite)) {
      const id = evidenceIdOf(ref)
      const hit = id ? targetsById.get(id) : undefined
      if (!id || !hit || seen.has(id)) {
        continue
      }
      seen.add(id)
      sources.push(citationSourceForTarget(hit.target, hit.index + 1, sourceContext, null, claim))
    }
    return sources
  }

  const resolveKpiSources = (kpi: RichKpi) => sourcesFor(kpi.cite, `${kpi.label}: ${kpi.value}`)

  const resolveCardSources = (card: RichCard) => sourcesFor(card.cite, card.body)

  const assetImage = (assetRef: string): CitationImage | null => {
    const match = ASSET_REF.exec(assetRef)
    const asset = match ? assetsByToken.get(match[1]) : undefined
    const mimeType = asset?.ref.mimeType
    // C2: a `url` image is https only, asset urls included.
    const src = httpsImageSrc(asset?.ref.publicUrl)
    if (!src || (mimeType && !mimeType.startsWith('image/'))) {
      return null
    }
    return { kind: 'url', src }
  }

  // The social posts a card talks about: the ones it cites, then the ones
  // its text links to, each once. Stored posts only (never a raw link).
  const resolveCardPosts = (card: RichCard): Array<SocialCitationPost> => {
    const posts: Array<SocialCitationPost> = []
    const seen = new Set<string>()
    const add = (post: SocialCitationPost | null | undefined) => {
      if (post && !seen.has(post.postToken)) {
        seen.add(post.postToken)
        posts.push(post)
      }
    }
    for (const ref of citeList(card.cite)) {
      const id = evidenceIdOf(ref)
      const target = id ? targetsById.get(id)?.target : undefined
      if (target?.kind === 'document' && target.sourceKind === 'social') {
        add(citationSocialPost(target.citation))
      }
    }
    for (const url of markdownLinkUrls(`${card.title}\n${card.body}`)) {
      add(linkedPosts?.get(url))
    }
    return posts
  }

  // A missing `image` is the same display as `"source"`: each cited source's
  // picture, in order. The model writes `image` only for `asset:<token>`.
  const resolveCardImages = (card: RichCard): Array<CitationImage> => {
    const assetRef = card.image && card.image !== 'source' ? card.image : null
    if (assetRef) {
      const image = assetImage(assetRef)
      return image ? [image] : []
    }
    if (cardImages !== undefined && citeList(card.cite).some(ref => {
      const id = evidenceIdOf(ref)
      return id ? targetsById.get(id)?.target.kind === 'web' : false
    })) {
      const selected = cardImages.find(image => sourceCardImageKey(image.card) === sourceCardImageKey(card))
      const src = sourceCardImageSrc(selected?.imageUrl)
      return src ? [{ kind: 'url', src, width: selected?.width, height: selected?.height, sourceUrl: httpsImageSrc(selected?.sourceUrl) }] : []
    }
    const images: Array<CitationImage> = []
    for (const ref of citeList(card.cite)) {
      const id = evidenceIdOf(ref)
      const target = id ? targetsById.get(id)?.target : undefined
      const image = target ? citationImage(target, sourceContext) : null
      if (image) {
        images.push(image)
      }
    }
    return images
  }

  if (!parsed) {
    if (!isPending) {
      return null
    }
    return (
      <div
        className="my-3 flex items-center gap-2.5"
        role="status"
        data-testid="thread-rich-block-preparing"
      >
        <ThreadStepGlyph state="active" testIdPrefix="thread-rich-block" />
        <Text as="span" size="sm" color="muted">
          {getThreadPlaceholders(language).preparingCanvas}
        </Text>
      </div>
    )
  }
  if (parsed.kind === 'kpis') {
    return <KpiRow kpis={parsed.kpis} resolveSources={resolveKpiSources} />
  }
  return (
    <TrendCards
      cards={parsed.cards}
      resolveSources={resolveCardSources}
      resolveImages={resolveCardImages}
      isImagePending={(card) => Boolean(sourceImagesLoading) && (!card.image || card.image === 'source')
        && citeList(card.cite).some(ref => {
          const id = evidenceIdOf(ref)
          return id ? targetsById.get(id)?.target.kind === 'web' : false
        })}
      resolvePosts={resolveCardPosts}
    />
  )
}
